/**
 * An admin's Add a game sheet (ISSUE-1292), after v1's "Add new grid" mode
 * of strip_game_bottom_sheet.dart: a day, then a game on that day. The new
 * game gets its own card with S1 and the switcher selects it. Free for now
 * (owner, 2026-11-01).
 *
 * ISSUE-1299 adds the second kind, a custom game with no feed, after v1's
 * custom_pool_match_form.dart: the admin types a name (or leaves it blank
 * for "Custom game N", which the server picks) and picks winners by hand.
 */
import {
  Button,
  confirmDestructive,
  Dialog,
  Input,
  SegmentedControl,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  STRIP_ADD_GAME_CONFIRM_LABEL,
  STRIP_ADD_GAME_HINT,
  STRIP_ADD_GAME_SOURCE_CUSTOM,
  STRIP_ADD_GAME_SOURCE_LABEL,
  STRIP_ADD_GAME_SOURCE_SCHEDULE,
  STRIP_ADD_GAME_TAKEN_HINT,
  STRIP_ADD_GAME_TITLE,
  STRIP_CANCEL_LABEL,
  STRIP_CUSTOM_GAME_HINT,
  STRIP_CUSTOM_GAME_NAME_HINT,
  STRIP_CUSTOM_GAME_NAME_LABEL,
  STRIP_CUSTOM_GAME_NAME_MAX_LENGTH,
  STRIP_CUSTOM_GAME_NAME_PLACEHOLDER,
  STRIP_DISCARD_GAME_NAME_BODY,
  STRIP_DISCARD_LABEL,
  STRIP_DISCARD_TITLE,
} from "./stripCardCopy";
import { StripGamePicker, type typeStripGamePick } from "./StripGamePicker";

const EMPTY_PICK: typeStripGamePick = { day: null, eventId: null };

type typeSource = "schedule" | "custom";

export type typeStripAddGameSheetProps = {
  open: boolean;
  leagueId: string;
  /**
   * ISSUE-1299. False for a pool made with no league: it has no schedule,
   * so the sheet adds a custom game only and shows no kind switch.
   */
  hasLeague?: boolean;
  takenEventIds: readonly string[];
  isBusy: boolean;
  onClose: () => void;
  onAdd: (eventId: string) => Promise<boolean>;
  onAddCustom: (title: string) => Promise<boolean>;
};

export const StripAddGameSheet = (
  props: typeStripAddGameSheetProps,
): ReactNode => {
  const hasLeague = props.hasLeague ?? true;
  const initialSource: typeSource = hasLeague ? "schedule" : "custom";
  const [pick, setPick] = useState<typeStripGamePick>(EMPTY_PICK);
  const [source, setSource] = useState<typeSource>(initialSource);
  const [customTitle, setCustomTitle] = useState("");

  useEffect(() => {
    if (!props.open) {
      setPick(EMPTY_PICK);
      setSource(initialSource);
      setCustomTitle("");
    }
  }, [props.open, initialSource]);

  const isCustom = !hasLeague || source === "custom";
  const canAdd = isCustom || pick.eventId !== null;
  const isDirty = isCustom && customTitle.trim() !== "";

  async function requestClose(): Promise<void> {
    if (!isDirty) {
      props.onClose();
      return;
    }
    const isConfirmed = await confirmDestructive({
      title: STRIP_DISCARD_TITLE,
      body: STRIP_DISCARD_GAME_NAME_BODY,
      confirmLabel: STRIP_DISCARD_LABEL,
    });
    if (isConfirmed) props.onClose();
  }

  function add(): void {
    const write = isCustom
      ? props.onAddCustom(customTitle)
      : pick.eventId === null
        ? null
        : props.onAdd(pick.eventId);
    if (write === null) return;
    void write.then((isDone) => {
      if (isDone) props.onClose();
    });
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={STRIP_ADD_GAME_TITLE}
      description={isCustom ? STRIP_CUSTOM_GAME_HINT : STRIP_ADD_GAME_HINT}
      autoFocus={false}
      isDirty={isDirty}
      onRequestClose={() => {
        void requestClose();
      }}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              void requestClose();
            }}
          >
            {STRIP_CANCEL_LABEL}
          </Button>
          <Button
            variant="primary"
            data-testid="strip-add-game-confirm"
            disabled={!canAdd}
            loading={props.isBusy}
            onClick={add}
          >
            {STRIP_ADD_GAME_CONFIRM_LABEL}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="strip-add-game-sheet">
        {hasLeague ? (
          <SegmentedControl
            label={STRIP_ADD_GAME_SOURCE_LABEL}
            testId="strip-add-game-source"
            fullWidth
            options={[
              { value: "schedule", label: STRIP_ADD_GAME_SOURCE_SCHEDULE },
              { value: "custom", label: STRIP_ADD_GAME_SOURCE_CUSTOM },
            ]}
            value={source}
            onValueChange={(next) => {
              setSource(next === "custom" ? "custom" : "schedule");
            }}
          />
        ) : null}
        {!props.open ? null : isCustom ? (
          <Input
            label={STRIP_CUSTOM_GAME_NAME_LABEL}
            hint={STRIP_CUSTOM_GAME_NAME_HINT}
            placeholder={STRIP_CUSTOM_GAME_NAME_PLACEHOLDER}
            maxLength={STRIP_CUSTOM_GAME_NAME_MAX_LENGTH}
            value={customTitle}
            data-testid="strip-custom-game-name"
            onChange={(event) => {
              setCustomTitle(event.target.value);
            }}
          />
        ) : (
          <StripGamePicker
            leagueId={props.leagueId}
            value={pick}
            onChange={setPick}
            takenEventIds={props.takenEventIds}
            takenHint={STRIP_ADD_GAME_TAKEN_HINT}
          />
        )}
      </div>
    </Dialog>
  );
};
